import React, { useMemo } from 'react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';

import GroupedItem from './components/GroupedItem';
import { Container, List } from './styles';

interface GroupedItems {
  letter: string;
  items: GroupedListModel[];
}

export interface Props {
  items: GroupedListModel[];
  isAlphabetic?: boolean;
  isSelectable?: boolean;
  lineGroup?: boolean;
  selectableType?: 'arrow' | 'check' | 'radio' | 'trashcan' | null;
  currentItem?: GroupedListModel[];
  isSearch?: boolean;
  subItems?: boolean;
  selectedQuantityKey?: string;
  onSelection: (user: GroupedListModel) => void;
  singleSelect?: boolean;
  hasFavorites?: boolean;
}

const GroupedList = ({
  lineGroup,
  items,
  isSelectable,
  isAlphabetic,
  currentItem,
  selectableType,
  isSearch,
  onSelection,
  subItems,
  selectedQuantityKey,
  singleSelect,
  hasFavorites,
}: Props) => {
  const { t } = useStoreTranslation();

  const aggregationQuantity =
    currentItem &&
    htmlT(t, selectedQuantityKey || 'groupedList.selected', {
      count: currentItem?.length,
    });

  const alphabeticGroup = useMemo(
    () =>
      items
        .reduce((acc: GroupedItems[], curr: GroupedListModel) => {
          let groupName: string;
          let groupList: GroupedItems | undefined;

          if (hasFavorites && curr.favorite) {
            groupName = t('groupedList.favorites');
            groupList = acc.find((name) => name.letter && name.letter === groupName);
          } else {
            groupName = curr.name ? curr.name[0].toUpperCase() : '';
            groupList = acc.find((name) => name.letter && name.letter.toUpperCase() === groupName);
          }

          if (groupList) {
            groupList.items.push(curr);
          } else {
            acc.push({
              letter: groupName,
              items: [curr],
            });
          }
          return acc;
        }, [])
        .sort((a, b) => {
          if (a.letter === t('groupedList.favorites')) return -1;
          if (b.letter === t('groupedList.favorites')) return 1;
          return a.letter < b.letter ? -1 : 1;
        }),
    [items, hasFavorites, t],
  );

  return (
    <Container>
      {!isSearch && !singleSelect && isSelectable && currentItem?.length !== 0 && (
        <List noPadding>
          <GroupedItem
            lineGroup={lineGroup}
            items={currentItem}
            selectedItems={currentItem}
            selectableType={selectableType}
            onSelection={onSelection}
            quantityText={aggregationQuantity}
            subItems={subItems}
            maximumItemsCount={3}
            hasFavorites={hasFavorites}
          />
        </List>
      )}
      {isAlphabetic && [...items, ...(currentItem || [])].length >= 10 ? (
        alphabeticGroup?.map((groupItem: GroupedItems, index: number) => (
          <List key={index} noPadding>
            <GroupedItem
              lineGroup={lineGroup}
              items={groupItem.items}
              selectableType={selectableType}
              onSelection={onSelection}
              selectedItems={currentItem}
              letter={groupItem.letter}
              isSearch={isSearch}
              subItems={subItems}
              hasFavorites={hasFavorites}
            />
          </List>
        ))
      ) : (
        <List noPadding>
          <GroupedItem
            lineGroup={lineGroup}
            items={items}
            selectableType={selectableType}
            onSelection={onSelection}
            selectedItems={currentItem}
            isSearch={isSearch}
            subItems={subItems}
            hasFavorites={hasFavorites}
          />
        </List>
      )}
    </Container>
  );
};

export default GroupedList;
